// SPECS: the maker's spec sheet for every AC in catalogue.js, keyed by model
// id. NO imports: scripts/prerender.js loads this file by stripping
// import/export, so the live compare page and the pre-rendered HTML read the
// same numbers.
//
// SOURCE: Daikin India's own product page for each model, the table under
// "Specifications" (daikinindia.com/<indoor model>, e.g. /ftkl50xv), read
// 21-10-2026 for the exact indoor unit we sell (F code; the G code is the
// same unit without the pipe kit). Never type a number that is not on that
// page. null = the page does not give it; the compare page then shows
// "Not given by brand" and hides a row no AC has.
//
// Fields
//   minKw, maxKw   inverter cooling range, "Cooling Capacity (Min ~ Max)".
//                  null on fixed-speed models (the page shows "x").
//   currentA       operating current, cooling.
//   cfm            air flow, cooling.
//   db             "Minimum IDU Sound Level (dBA)".
//   idu, odu       dimensions H x W x D in mm.
//   pipe           [max length m, max height m].
//   stabiliser     'inside' (Stabilizer Inside) or 'free' (Stabilizer Free,
//                  with the voltage range the page gives).
//   heat           heating: { kw, minC, maxC } on Hot & Cold models.
//   f              the feature marks: true for a tick, false for a cross,
//                  a string where the page names the feature instead.
// The full-cooling and highest outside temperatures stay in SERIES
// (data/highlights.js), which the product pages already read.

const DAIKIN_PAGE = (code) => `https://daikinindia.com/${code}`;

// Feature marks shared by a whole series on Daikin's pages.
const F_GTQ = {
  swing: false, streamer: false, ieye: false, wifi: false, voice: false,
  air3d: false, triple: false, flaps: true, chill: true, econo: true, backlit: false,
  dew: false, selfDiag: true, filterAlert: false, prefilter: false, smell: true,
  coating: true, displayOff: false, childLock: true,
  pm10: false, pm25: true, carbon: false,
};
const F_GTKC = {
  swing: true, streamer: false, ieye: false, wifi: false, voice: false,
  air3d: false, triple: true, flaps: true, chill: true, econo: true, backlit: false,
  dew: true, selfDiag: true, filterAlert: true, prefilter: true, smell: true,
  coating: true, displayOff: true, childLock: true,
  pm10: false, pm25: false, carbon: false,
};
const F_GTKL = {
  swing: true, streamer: false, ieye: false, wifi: false, voice: false,
  air3d: true, triple: true, flaps: true, chill: true, econo: true, backlit: true,
  dew: true, selfDiag: true, filterAlert: true, prefilter: true, smell: true,
  coating: true, displayOff: true, childLock: true,
  pm10: false, pm25: true, carbon: true,
};
const F_GTKM = { ...F_GTKL };
const F_FTKU = { ...F_GTKL, air3d: false, carbon: false };
const F_FTHT = { ...F_GTKL, chill: 'Power Full mode', pm25: false, pm10: true, carbon: false };
const F_JTKJ = { ...F_GTKL, streamer: true, ieye: true, wifi: true, voice: true, pm25: false, pm10: true, carbon: false };

const S = (code, o) => ({ src: DAIKIN_PAGE(code), ...o });

export const SPECS = {
  'gtq-28': S('ftq28xv', { minKw: null, maxKw: null, currentA: 3.22, cfm: 420, db: 32, idu: [298, 800, 229], odu: [550, 675, 284], pipe: [20, 10], stabiliser: 'free', volts: '160 to 264 V', f: F_GTQ }),
  'gtq-35': S('ftq35xv', { minKw: null, maxKw: null, currentA: 3.85, cfm: 420, db: 35, idu: [298, 800, 229], odu: [550, 675, 284], pipe: [20, 10], stabiliser: 'free', volts: '160 to 264 V', f: F_GTQ }),
  'gtq-50': S('ftq50xv', { minKw: null, maxKw: null, currentA: 5.8, cfm: 523, db: 38, idu: [298, 885, 229], odu: [595, 845, 300], pipe: [20, 10], stabiliser: 'free', volts: '160 to 264 V', f: F_GTQ }),

  'gtkc-35': S('ftkc35xv', { minKw: 1.0, maxKw: 3.79, currentA: 4.95, cfm: 445, db: 27, idu: [298, 800, 229], odu: [550, 675, 284], pipe: [15, 12], stabiliser: 'inside', f: F_GTKC }),
  'gtkc-50': S('ftkc50xv', { minKw: 1.5, maxKw: 5.3, currentA: 7.17, cfm: 551, db: 32, idu: [298, 885, 229], odu: [595, 845, 300], pipe: [20, 16], stabiliser: 'inside', f: F_GTKC }),

  'gtkl-35': S('ftkl35xv', { minKw: 1.0, maxKw: 3.79, currentA: 4.95, cfm: 445, db: 27, idu: [298, 800, 239], odu: [550, 675, 284], pipe: [15, 12], stabiliser: 'inside', f: F_GTKL }),
  'gtkl-50': S('ftkl50xv', { minKw: 1.4, maxKw: 5.3, currentA: 7.17, cfm: 551, db: 32, idu: [298, 885, 239], odu: [595, 845, 300], pipe: [20, 16], stabiliser: 'inside', f: F_GTKL }),
  'gtkl-60': S('ftkl60xv', { minKw: 1.75, maxKw: 6.3, currentA: 8.29, cfm: 597, db: 32, idu: [298, 885, 239], odu: [595, 845, 300], pipe: [25, 20], stabiliser: 'inside', f: F_GTKL }),
  'gtkl-71': S('ftkl71xv', { minKw: 1.4, maxKw: 7.5, currentA: 10.01, cfm: 742, db: 32, idu: [325, 1060, 278], odu: [595, 845, 300], pipe: [30, 20], stabiliser: 'inside', f: F_GTKL }),
  'gtkl-90': S('ftkl90xv', { minKw: 2.4, maxKw: 9.5, currentA: 11.8, cfm: 812, db: 37, idu: [325, 1220, 278], odu: [695, 930, 350], pipe: [25, 20], stabiliser: 'inside', f: F_GTKL }),

  'gtkm-35': S('ftkm35xv', { minKw: 1.0, maxKw: 4.3, currentA: 3.8, cfm: 523, db: 31, idu: [298, 892, 231], odu: [550, 675, 284], pipe: [15, 12], stabiliser: 'inside', f: F_GTKM }),
  'gtkm-50': S('ftkm50xv', { minKw: 1.58, maxKw: 5.9, currentA: 5.51, cfm: 685, db: 31, idu: [325, 1065, 280], odu: [595, 845, 300], pipe: [20, 16], stabiliser: 'inside', f: F_GTKM }),
  'gtkm-60': S('ftkm60xv', { minKw: 2.0, maxKw: 7.0, currentA: 6.54, cfm: 752, db: 31, idu: [325, 1065, 280], odu: [695, 930, 350], pipe: [25, 20], stabiliser: 'inside', f: F_GTKM }),

  'ftku-50': S('ftku50xv', { minKw: 1.58, maxKw: 5.9, currentA: 5.51, cfm: 685, db: 31, idu: [325, 1060, 284], odu: [595, 845, 300], pipe: [20, 16], stabiliser: 'inside', f: F_FTKU }),

  'ftht-35': S('ftht35xv', { minKw: 1.0, maxKw: 3.79, currentA: 5.03, cfm: 392, db: 27, idu: [298, 800, 239], odu: [550, 675, 284], pipe: [15, 12], stabiliser: 'inside', heat: { kw: 3.52, minC: -10, maxC: 24 }, f: F_FTHT }),
  'ftht-50': S('ftht50xv', { minKw: 1.4, maxKw: 5.3, currentA: 7.17, cfm: 526, db: 32, idu: [298, 885, 239], odu: [595, 845, 300], pipe: [20, 16], stabiliser: 'inside', heat: { kw: 5.0, minC: -10, maxC: 24 }, f: F_FTHT }),
  'ftht-60': S('ftht60xv', { minKw: 1.75, maxKw: 6.5, currentA: 8.29, cfm: 540, db: 32, idu: [298, 885, 239], odu: [595, 845, 300], pipe: [25, 20], stabiliser: 'inside', heat: { kw: 6.0, minC: -10, maxC: 24 }, f: F_FTHT }),

  'jtkj-35': S('jtkj35xv', { minKw: 1.0, maxKw: 4.3, currentA: 3.8, cfm: 523, db: 31, idu: [298, 892, 231], odu: [550, 675, 284], pipe: [15, 12], stabiliser: 'inside', f: F_JTKJ }),
  'jtkj-53': S('jtkj53xv', { minKw: 1.58, maxKw: 6.0, currentA: 5.93, cfm: 713, db: 31, idu: [325, 1065, 280], odu: [595, 845, 300], pipe: [20, 16], stabiliser: 'inside', f: F_JTKJ }),
  'jtkj-60': S('jtkj60xv', { minKw: 2.0, maxKw: 7.0, currentA: 6.54, cfm: 752, db: 31, idu: [325, 1065, 280], odu: [695, 930, 350], pipe: [25, 20], stabiliser: 'inside', f: F_JTKJ }),
};
